import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  LineChart, 
  Line, 
  AreaChart, 
  Area, 
  BarChart, 
  Bar, 
  PieChart, 
  Pie, 
  Cell,
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer 
} from 'recharts';
import { 
  Play, 
  Pause, 
  CheckCircle, 
  Edit, 
  TrendingUp, 
  Users, 
  Target,
  BarChart3,
  PlusCircle,
  Eye,
  TestTube
} from 'lucide-react';
import { ABTestingService, ABExperiment, ExperimentResult } from '@/services/ab-testing/abTestingService';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const COLORS = [
  'hsl(var(--primary))', 
  'hsl(var(--secondary))', 
  'hsl(var(--accent))', 
  'hsl(var(--muted))',
  'hsl(var(--destructive))',
  'hsl(var(--warning))'
];

export const ABTestDashboard: React.FC = () => {
  const [experiments, setExperiments] = useState<ABExperiment[]>([]);
  const [results, setResults] = useState<Record<string, ExperimentResult>>({});
  const [loading, setLoading] = useState(true);
  const [selectedExperiment, setSelectedExperiment] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      console.log('Loading A/B Testing data...');
      // For demo purposes, we'll show all experiments
      // In production, you'd want admin-only access
      const allExperiments = await ABTestingService.getActiveExperiments();
      console.log('Loaded experiments:', allExperiments);
      setExperiments(allExperiments);

      // Load results for each experiment
      const resultsPromises = allExperiments.map(async (exp) => {
        const result = await ABTestingService.getExperimentResults(exp.id);
        return { id: exp.id, result };
      });

      const resultsData = await Promise.all(resultsPromises);
      const resultsMap: Record<string, ExperimentResult> = {};
      
      resultsData.forEach(({ id, result }) => {
        if (result) {
          resultsMap[id] = result;
        }
      });

      setResults(resultsMap);
    } catch (error) {
      console.error('Error loading A/B test data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (experimentId: string, newStatus: 'active' | 'paused' | 'completed') => {
    const success = await ABTestingService.updateExperimentStatus(experimentId, newStatus);
    if (success) {
      loadData(); // Reload data
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-primary text-primary-foreground';
      case 'paused': return 'bg-secondary text-secondary-foreground';
      case 'completed': return 'bg-accent text-accent-foreground';
      case 'draft': return 'bg-muted text-muted-foreground';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active': return <Play className="h-4 w-4" />;
      case 'paused': return <Pause className="h-4 w-4" />;
      case 'completed': return <CheckCircle className="h-4 w-4" />;
      case 'draft': return <Edit className="h-4 w-4" />;
      default: return <Edit className="h-4 w-4" />;
    }
  };

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardHeader className="pb-2">
                <div className="h-4 bg-muted rounded w-3/4"></div>
              </CardHeader>
              <CardContent>
                <div className="h-8 bg-muted rounded w-1/2 mb-2"></div>
                <div className="h-3 bg-muted rounded w-2/3"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  const activeExperiments = experiments.filter(exp => exp.status === 'active');
  const totalParticipants = Object.values(results).reduce((sum, result) => sum + result.total_participants, 0);
  const averageConversionRate = Object.values(results).length > 0 
    ? Object.values(results).reduce((sum, result) => {
        const avgRate = result.variants.reduce((vSum, v) => vSum + v.conversion_rate, 0) / result.variants.length;
        return sum + avgRate;
      }, 0) / Object.values(results).length
    : 0;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">A/B Testing Dashboard</h1>
          <p className="text-muted-foreground">Manage and analyze experiments</p>
        </div>
        <CreateExperimentDialog onExperimentCreated={loadData} />
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Aktive tester</CardTitle>
            <Target className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{activeExperiments.length}</div>
            <p className="text-xs text-muted-foreground">Løpende eksperimenter</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Totale deltakere</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalParticipants.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">På tvers av alle tester</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Gjennomsnittlig konvertering</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{averageConversionRate.toFixed(1)}%</div>
            <p className="text-xs text-muted-foreground">Alle varianter</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Statistisk signifikans</CardTitle>
            <BarChart3 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {Object.values(results).filter(r => r.statistical_significance).length}
            </div>
            <p className="text-xs text-muted-foreground">Signifikante resultater</p>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <Tabs defaultValue="experiments" className="space-y-4">
        <TabsList>
          <TabsTrigger value="experiments">Eksperimenter</TabsTrigger>
          <TabsTrigger value="results">Resultater</TabsTrigger>
          <TabsTrigger value="analytics">Analyse</TabsTrigger>
        </TabsList>

        <TabsContent value="experiments" className="space-y-4">
          {experiments.length === 0 ? (
            <div className="text-center py-12">
              <TestTube className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">Ingen A/B tester funnet</h3>
              <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                Kom i gang med A/B testing ved å opprette ditt første eksperiment. 
                Test ulike versjoner av innhold for å optimalisere brukeropplevelsen.
              </p>
              <CreateExperimentDialog onExperimentCreated={loadData}>
                <Button>
                  <PlusCircle className="h-4 w-4 mr-2" />
                  Opprett første A/B test
                </Button>
              </CreateExperimentDialog>
            </div>
          ) : (
          <div className="grid grid-cols-1 gap-4">
            {experiments.map((experiment) => (
              <Card key={experiment.id}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        {experiment.name}
                        <Badge className={getStatusColor(experiment.status)}>
                          {getStatusIcon(experiment.status)}
                          {experiment.status}
                        </Badge>
                      </CardTitle>
                      <CardDescription>{experiment.description}</CardDescription>
                    </div>
                    <div className="flex gap-2">
                      {experiment.status === 'active' && (
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleStatusChange(experiment.id, 'paused')}
                        >
                          <Pause className="h-4 w-4 mr-2" />
                          Pause
                        </Button>
                      )}
                      {experiment.status === 'paused' && (
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleStatusChange(experiment.id, 'active')}
                        >
                          <Play className="h-4 w-4 mr-2" />
                          Resume
                        </Button>
                      )}
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => setSelectedExperiment(experiment.id)}
                      >
                        <Eye className="h-4 w-4 mr-2" />
                        View Results
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                {results[experiment.id] && (
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="text-center">
                        <div className="text-2xl font-bold">
                          {results[experiment.id].total_participants}
                        </div>
                        <p className="text-sm text-muted-foreground">Deltakere</p>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold">
                          {results[experiment.id].variants.length}
                        </div>
                        <p className="text-sm text-muted-foreground">Varianter</p>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold">
                          {results[experiment.id].statistical_significance ? '✓' : '✗'}
                        </div>
                        <p className="text-sm text-muted-foreground">Signifikant</p>
                      </div>
                    </div>
                  </CardContent>
                )}
              </Card>
            ))}
          </div>
          )}
        </TabsContent>

        <TabsContent value="results" className="space-y-4">
          {selectedExperiment && results[selectedExperiment] && (
            <ExperimentResultsView result={results[selectedExperiment]} />
          )}
        </TabsContent>

        <TabsContent value="analytics" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Test Performance Overview</CardTitle>
              <CardDescription>Conversion rates across all experiments</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={Object.values(results).map(result => ({
                  name: result.experiment.name,
                  ...result.variants.reduce((acc, variant, index) => {
                    acc[variant.variant.name] = variant.conversion_rate;
                    return acc;
                  }, {} as any)
                }))}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  {COLORS.map((color, index) => (
                    <Bar key={index} dataKey={`variant_${index}`} fill={color} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

// Component for displaying detailed experiment results
const ExperimentResultsView: React.FC<{ result: ExperimentResult }> = ({ result }) => (
  <Card>
    <CardHeader>
      <CardTitle>{result.experiment.name} - Detailed Results</CardTitle>
      <CardDescription>Comprehensive analysis of experiment performance</CardDescription>
    </CardHeader>
    <CardContent className="space-y-6">
      {/* Variant Comparison */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {result.variants.map((variant, index) => (
          <Card key={variant.variant.id}>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">{variant.variant.name}</CardTitle>
                {variant.variant.is_control && (
                  <Badge variant="secondary">Control</Badge>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm">Participants:</span>
                  <span className="font-medium">{variant.participants}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm">Conversions:</span>
                  <span className="font-medium">{variant.conversions}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm">Conversion Rate:</span>
                  <span className="font-medium text-primary">
                    {variant.conversion_rate.toFixed(2)}%
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Conversion Chart */}
      <Card>
        <CardHeader>
          <CardTitle>Conversion Rate Comparison</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={result.variants.map(v => ({
              name: v.variant.name,
              conversion_rate: v.conversion_rate,
              participants: v.participants
            }))}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="conversion_rate" fill="hsl(var(--primary))" />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </CardContent>
  </Card>
);

// Dialog for creating new experiments
const CreateExperimentDialog: React.FC<{ onExperimentCreated: () => void; children?: React.ReactNode }> = ({ onExperimentCreated, children }) => {
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    traffic_percentage: 100
  });

  const handleSubmit = async () => {
    if (!formData.name) return;

    const experimentId = await ABTestingService.createExperiment(formData);
    if (experimentId) {
      setOpen(false);
      setFormData({ name: '', description: '', traffic_percentage: 100 });
      onExperimentCreated();
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {children || (
        <Button>
          <PlusCircle className="h-4 w-4 mr-2" />
          New Experiment
        </Button>
        )}
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create New A/B Test</DialogTitle>
          <DialogDescription>
            Set up a new experiment to test different variations
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label htmlFor="name">Experiment Name</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              placeholder="e.g., Homepage CTA Button Test"
            />
          </div>
          <div>
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Describe what you're testing..."
            />
          </div>
          <div>
            <Label htmlFor="traffic">Traffic Percentage</Label>
            <Input
              id="traffic"
              type="number"
              min="1"
              max="100"
              value={formData.traffic_percentage}
              onChange={(e) => setFormData(prev => ({ ...prev, traffic_percentage: parseInt(e.target.value) }))}
            />
          </div>
          <Button onClick={handleSubmit} className="w-full">
            Create Experiment
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};